import { memo, startTransition, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, ChevronRight, Pin, X } from "lucide-react"
import { type OpenTab, getTabIcon, getExplorerTabTitle } from "@/components/features/document-tabs"
import {
  TabContextMenuHost,
  type TabMenuHandlers,
} from "@/components/features/tab-context-menu-host"
import { usePersistedState } from "@/hooks/use-persisted-state"
import { StorageKeys, StorageSchemas } from "@/lib/storage"
import { testIds } from "@/lib/test-ids"
import { cn } from "@/lib/utils"
import { useWorkspace, type Workspace } from "@/providers/workspace-provider"
import { WorkspaceIcon } from "./left-sidebar.components"
import {
  useWorkspaceRowContextMenu,
  WorkspaceRowContextMenuPortal,
} from "./left-sidebar.workspace-row-menu"
import { isSoleHomeTab, resolveTabWorkspaceId, isTabVisibleForActiveWorkspaces } from "@/components/layout/resizable-layout.handlers/shared"
import { SHELL_COLUMN_FOOTER_CLASS, SHELL_COLUMN_HEADER_PX } from "@/lib/shell-chrome"
import { WS_ICON_ON_ACCENT, workspaceAccentFill } from "@/lib/workspace-color"

const UNGROUPED_KEY = "__ungrouped__"

/** Soft max when auto: ~8 tab rows, also capped as a fraction of the sidebar column. */
const OPEN_TABS_SOFT_MAX_PX = 280
const OPEN_TABS_SOFT_MAX_FRAC = 0.35
/** Absolute drag ceiling as a fraction of the column. */
const OPEN_TABS_HARD_MAX_FRAC = 0.6
/** Body floor when expanded (one tab row). */
const OPEN_TABS_MIN_BODY_PX = 36

export type OpenTabsWorkspaceGroup = {
  ws: Workspace | null
  tabs: OpenTab[]
}

/** Group open tabs by workspace. Global shell tabs (home, inbox, …) go in an ungrouped block first.
 * Drive index/preview tabs belong to the workspace of the connected Drive account, not App. */
export function groupOpenTabsByWorkspace(
  openTabs: OpenTab[],
  workspaces: Workspace[],
  activeWorkspaceIds: string[],
): OpenTabsWorkspaceGroup[] {
  const activeWsSet = new Set(activeWorkspaceIds)
  const wsMap = new Map(workspaces.map((w) => [w.id, w]))
  const defaultWorkWs = workspaces.find((w) => w.type === "work" && activeWsSet.has(w.id))
  const wsGroups = new Map<string, OpenTab[]>()

  for (const tab of openTabs) {
    if (!isTabVisibleForActiveWorkspaces(tab, workspaces, activeWorkspaceIds)) {
      continue
    }
    // Global shell tabs live outside workspace groups (same as top tab bar app group).
    if (
      tab.type === "home" ||
      tab.type === "inbox" ||
      tab.type === "graph-view" ||
      tab.type === "edit-history" ||
      tab.type === "search-view" ||
      tab.type === "person-view"
    ) {
      const grp = wsGroups.get(UNGROUPED_KEY) ?? []
      grp.push(tab)
      wsGroups.set(UNGROUPED_KEY, grp)
      continue
    }
    let wsId = resolveTabWorkspaceId(tab, workspaces) ?? null
    if (!wsId && tab.type?.startsWith("integration-") && defaultWorkWs) {
      wsId = defaultWorkWs.id
    }
    if (!wsId) wsId = UNGROUPED_KEY
    if (!wsMap.has(wsId) && wsId !== UNGROUPED_KEY) continue
    const grp = wsGroups.get(wsId) ?? []
    grp.push(tab)
    wsGroups.set(wsId, grp)
  }

  const sortedWsIds = [...wsGroups.keys()]
    .filter((id) => id !== UNGROUPED_KEY)
    .sort((a, b) => (wsMap.get(a)?.name ?? "").localeCompare(wsMap.get(b)?.name ?? ""))
  // Global / ungrouped first so Home & Inbox sit above workspace groups.
  if (wsGroups.has(UNGROUPED_KEY)) sortedWsIds.unshift(UNGROUPED_KEY)

  return sortedWsIds.map((wsId) => {
    const tabs = wsGroups.get(wsId) ?? []
    return {
      ws: wsId === UNGROUPED_KEY ? null : (wsMap.get(wsId) ?? null),
      tabs,
    }
  })
}

/**
 * Open Tabs drawer at the bottom of the mode column (Decision #8, revised 2026-11-01).
 * Collapsed, the header is the column footer and lines up with rail Settings.
 * Expanded, rows grow upward over the lens.
 *
 * Semi-auto height: hug content by default up to a soft max; overflow scrolls.
 * Drag sets a px ceiling (persisted); double-click handle clears back to auto.
 */
export const SidebarOpenTabsSection = memo(function SidebarOpenTabsSection({
  splitLayoutContainerRef,
  hasOpenTabs,
  tabsByWorkspace,
  openTabsTotalCount,
  activeTabId,
  activeWorkspaceIds,
  onDocumentClick,
  onDocumentClose,
  onCloseManyDocuments,
  onToggleWorkspace,
  onHideOtherWorkspaces,
  tabMenuHandlers,
}: {
  splitLayoutContainerRef: React.RefObject<HTMLDivElement | null>
  hasOpenTabs: boolean
  tabsByWorkspace: OpenTabsWorkspaceGroup[]
  openTabsTotalCount: number
  activeTabId?: string | null
  activeWorkspaceIds: string[]
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  onDocumentClose?: (documentId: string) => void
  onCloseManyDocuments?: (documentIds: string[]) => void
  onToggleWorkspace?: (wsId: string) => void
  onHideOtherWorkspaces?: (wsId: string) => void
  /** Shared with horizontal tab bar (pin/close/others…). */
  tabMenuHandlers?: TabMenuHandlers
}) {
  const { workspaces } = useWorkspace()
  const workspacesRoute = useMemo(
    () =>
      workspaces.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaces],
  )
  const allVisibleTabs = useMemo(
    () => tabsByWorkspace.flatMap((g) => g.tabs),
    [tabsByWorkspace],
  )
  const { menu: wsRowMenu, menuRef: wsRowMenuRef, openMenu: openWsRowMenu, closeMenu: closeWsRowMenu } =
    useWorkspaceRowContextMenu()

  const listInnerRef = useRef<HTMLDivElement>(null)
  const [contentBodyPx, setContentBodyPx] = useState(0)
  const [columnPx, setColumnPx] = useState(0)
  /** Live drag ceiling; null when not dragging. */
  const [dragCapPx, setDragCapPx] = useState<number | null>(null)
  const dragCapPxRef = useRef<number | null>(null)

  const [userMaxPx, setUserMaxPx] = usePersistedState<number | null>({
    key: StorageKeys.BROWSE_OPEN_TABS_USER_MAX_PX,
    defaultValue: null,
    schema: StorageSchemas.numberNullable,
  })

  const [openTabsSectionCollapsed, setOpenTabsSectionCollapsed] = usePersistedState({
    key: StorageKeys.BROWSE_OPEN_TABS_COLLAPSED,
    defaultValue: false,
    schema: StorageSchemas.boolean,
  })

  // Measure list content + sidebar column for soft/hard max.
  useEffect(() => {
    const inner = listInnerRef.current
    const column = splitLayoutContainerRef.current
    if (!inner && !column) return

    const measure = () => {
      if (inner) setContentBodyPx(inner.scrollHeight)
      if (column) setColumnPx(column.getBoundingClientRect().height)
    }
    measure()

    const ro = new ResizeObserver(measure)
    if (inner) ro.observe(inner)
    if (column) ro.observe(column)
    return () => ro.disconnect()
  }, [splitLayoutContainerRef, openTabsTotalCount, openTabsSectionCollapsed, tabsByWorkspace])

  const headerPx = SHELL_COLUMN_HEADER_PX
  const softMax = Math.max(
    headerPx + OPEN_TABS_MIN_BODY_PX,
    Math.min(
      OPEN_TABS_SOFT_MAX_PX,
      columnPx > 0 ? Math.floor(columnPx * OPEN_TABS_SOFT_MAX_FRAC) : OPEN_TABS_SOFT_MAX_PX,
    ),
  )
  const hardMax = columnPx > 0 ? Math.floor(columnPx * OPEN_TABS_HARD_MAX_FRAC) : OPEN_TABS_SOFT_MAX_PX * 2
  const floorPx = headerPx + OPEN_TABS_MIN_BODY_PX
  const contentTotalPx = headerPx + contentBodyPx

  const effectiveCap =
    dragCapPx != null ? dragCapPx : userMaxPx != null ? userMaxPx : softMax
  const clampedCap = Math.min(hardMax, Math.max(floorPx, effectiveCap))
  // Ceiling: hug content when below the cap (no empty void).
  const panelPx = Math.min(contentTotalPx, clampedCap)
  const needsScroll = contentTotalPx > panelPx + 1

  const onVerticalHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return
      if (!splitLayoutContainerRef.current) return
      e.preventDefault()
      e.stopPropagation()
      const handle = e.currentTarget
      handle.setPointerCapture(e.pointerId)
      const startY = e.clientY
      const colH = splitLayoutContainerRef.current.getBoundingClientRect().height
      const hard = Math.floor(colH * OPEN_TABS_HARD_MAX_FRAC)
      const floor = headerPx + OPEN_TABS_MIN_BODY_PX
      const startCap = Math.min(hard, Math.max(floor, userMaxPx ?? panelPx))

      dragCapPxRef.current = startCap
      setDragCapPx(startCap)

      const onMove = (ev: PointerEvent) => {
        // Drawer grows upward: dragging the handle up increases the cap.
        const dy = startY - ev.clientY
        const next = Math.min(hard, Math.max(floor, startCap + dy))
        dragCapPxRef.current = next
        setDragCapPx(next)
      }
      const onUp = (ev: PointerEvent) => {
        try {
          handle.releasePointerCapture(ev.pointerId)
        } catch {
          /* ignore */
        }
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
        window.removeEventListener("pointercancel", onUp)
        const finalCap = dragCapPxRef.current
        dragCapPxRef.current = null
        setDragCapPx(null)
        if (finalCap == null) return
        // Near soft-max auto band with room for full content → clear to auto
        const soft =
          colH > 0
            ? Math.min(OPEN_TABS_SOFT_MAX_PX, Math.floor(colH * OPEN_TABS_SOFT_MAX_FRAC))
            : OPEN_TABS_SOFT_MAX_PX
        if (Math.abs(finalCap - soft) <= 12 && finalCap >= contentTotalPx - 4) {
          setUserMaxPx(null)
        } else {
          setUserMaxPx(finalCap)
        }
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
      window.addEventListener("pointercancel", onUp)
    },
    [splitLayoutContainerRef, userMaxPx, panelPx, headerPx, contentTotalPx, setUserMaxPx],
  )

  const onVerticalHandleDoubleClick = useCallback(() => {
    setUserMaxPx(null)
  }, [setUserMaxPx])

  if (!hasOpenTabs) return null

  return (
    <>
      {!openTabsSectionCollapsed && (
        <div
          role="separator"
          aria-orientation="horizontal"
          aria-label="Resize open tabs. Double-click to reset auto height."
          title="Drag to set max height. Double-click to reset."
          data-testid={testIds.leftSidebar.openTabsResizeHandle}
          className="group relative z-10 flex h-2 flex-shrink-0 cursor-row-resize touch-none select-none items-center justify-center border-t border-border transition-colors hover:border-primary/40 hover:bg-sidebar-accent/40"
          onPointerDown={onVerticalHandlePointerDown}
          onDoubleClick={onVerticalHandleDoubleClick}
        >
          <div className="pointer-events-none h-0.5 w-8 rounded bg-muted-foreground/30 transition-colors group-hover:bg-primary/50" />
        </div>
      )}
      <div
        data-testid={testIds.leftSidebar.openTabsSection}
        className={
          openTabsSectionCollapsed
            ? "flex flex-none flex-col"
            : "flex min-h-0 flex-col overflow-hidden"
        }
        style={
          openTabsSectionCollapsed
            ? undefined
            : {
                flex: `0 0 ${panelPx}px`,
                height: panelPx,
                maxHeight: panelPx,
              }
        }
      >
        {!openTabsSectionCollapsed && (
          <div
            id="sidebar-open-tabs-list"
            role="region"
            aria-labelledby="sidebar-open-tabs-heading"
            className={cn(
              "min-h-0 flex-1 overscroll-contain",
              needsScroll ? "overflow-y-auto" : "overflow-hidden",
            )}
          >
            <div ref={listInnerRef} className="px-2 pb-2">
              {tabsByWorkspace.map(({ ws, tabs }, gi) => {
                const wsColor = ws?.color ?? "hsl(var(--primary))"
                const groupDimmed = ws != null && !activeWorkspaceIds.includes(ws.id)
                const inactiveIconColor = "hsl(var(--muted-foreground) / 0.62)"
                return (
                  <div
                    key={ws?.id ?? `u-${gi}`}
                    className={cn(
                      "ws-tab-group group/tabgroup",
                      groupDimmed && "ws-tab-group--inactive",
                    )}
                    style={
                      groupDimmed
                        ? undefined
                        : ({ "--ws-color": wsColor } as React.CSSProperties)
                    }
                  >
                    {ws && (
                      <div
                        className="ws-tab-group__header flex items-center gap-1.5 px-1 py-1 mt-1.5"
                        onContextMenu={(e) => onToggleWorkspace && openWsRowMenu(ws.id, e)}
                      >
                        <span
                          className={cn(
                            "w-4 h-4 rounded flex items-center justify-center flex-none",
                            groupDimmed && "bg-muted-foreground/12",
                          )}
                          style={
                            groupDimmed
                              ? undefined
                              : { backgroundColor: workspaceAccentFill(wsColor) }
                          }
                        >
                          <WorkspaceIcon
                            icon={ws.icon}
                            color={groupDimmed ? inactiveIconColor : WS_ICON_ON_ACCENT}
                            name={ws.name}
                            size={10}
                          />
                        </span>
                        <span
                          className={cn(
                            "text-[11px] font-semibold uppercase tracking-wider truncate flex-1",
                            groupDimmed && "text-muted-foreground/70",
                          )}
                          style={groupDimmed ? undefined : { color: wsColor }}
                        >
                          {ws.name}
                          {groupDimmed && tabs.length > 0 && (
                            <span className="tabular-nums font-medium"> ({tabs.length})</span>
                          )}
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          {groupDimmed ? (
                            <span className="flex items-center opacity-0 group-hover/tabgroup:opacity-100 transition-opacity">
                              <button
                                type="button"
                                title={`Show ${ws.name} workspace`}
                                data-testid={testIds.leftSidebar.openTabsShowWorkspace(ws.id)}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  onToggleWorkspace?.(ws.id)
                                }}
                                className="text-[10px] font-medium text-muted-foreground hover:text-sidebar-foreground px-1 py-0.5 rounded hover:bg-sidebar-accent"
                              >
                                Show
                              </button>
                            </span>
                          ) : (
                            <span className="flex items-center opacity-0 group-hover/tabgroup:opacity-100 transition-opacity">
                              <button
                                type="button"
                                title="Close all"
                                data-testid={
                                  ws ? testIds.leftSidebar.openTabsCloseAll(ws.id) : undefined
                                }
                                onClick={(e) => {
                                  e.stopPropagation()
                                  const ids = tabs.map((t) => t.id)
                                  if (onCloseManyDocuments) {
                                    onCloseManyDocuments(ids)
                                    return
                                  }
                                  ids.forEach((id) => onDocumentClose?.(id))
                                }}
                                className="p-0.5 rounded hover:bg-sidebar-accent text-muted-foreground hover:text-sidebar-foreground"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    {!groupDimmed &&
                      tabs.map((tab) => {
                        const isUnpinned = !tab.isPinned
                        const title =
                          tab.type === "explorer"
                            ? getExplorerTabTitle(tab, workspacesRoute)
                            : tab.title
                        const rowHandlers: TabMenuHandlers = {
                          ...tabMenuHandlers,
                          onTabClose: tabMenuHandlers?.onTabClose ?? onDocumentClose,
                          onTabPinToggle: tabMenuHandlers?.onTabPinToggle,
                        }
                        return (
                          <TabContextMenuHost
                            key={tab.id}
                            tab={tab}
                            totalTabs={allVisibleTabs.length}
                            workspacesRoute={workspacesRoute}
                            handlers={rowHandlers}
                          >
                            {({ onContextMenu }) => (
                              <div
                                className={`ws-nav-item group flex items-center gap-2 pr-2 py-1 text-sm select-none ${
                                  activeTabId === tab.id
                                    ? "ws-nav-item--active text-sidebar-foreground"
                                    : "ws-nav-item--open"
                                }`}
                                style={{ paddingLeft: ws ? "20px" : "8px" }}
                                onClick={(e) => {
                                  if (ws && groupDimmed) onToggleWorkspace?.(ws.id)
                                  onDocumentClick?.(tab.id, e)
                                }}
                                onContextMenu={onContextMenu}
                                data-testid={testIds.leftSidebar.openTabsRow(tab.id)}
                              >
                                {tab.isPinned ? (
                                  <Pin
                                    className="h-2.5 w-2.5 flex-none text-muted-foreground"
                                    aria-label="Pinned tab"
                                  />
                                ) : null}
                                <span className="w-3.5 h-3.5 flex-none flex items-center justify-center [&>svg]:w-3 [&>svg]:h-3">
                                  {getTabIcon(tab.type)}
                                </span>
                                <span
                                  className={cn(
                                    "flex-1 truncate text-[13px]",
                                    isUnpinned && "italic text-muted-foreground/80",
                                  )}
                                >
                                  {title}
                                </span>
                                {!isSoleHomeTab(allVisibleTabs, tab.id) && (
                                  <button
                                    type="button"
                                    className="flex-none opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-sidebar-accent"
                                    title="Close tab"
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      onDocumentClose?.(tab.id)
                                    }}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </TabContextMenuHost>
                        )
                      })}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <div className={cn(SHELL_COLUMN_FOOTER_CLASS, "flex flex-none items-center border-t border-border px-2")}>
          <button
            type="button"
            aria-expanded={!openTabsSectionCollapsed}
            aria-controls="sidebar-open-tabs-list"
            id="sidebar-open-tabs-heading"
            onClick={() => {
              startTransition(() => setOpenTabsSectionCollapsed((c) => !c))
            }}
            className="flex h-full w-full min-w-0 items-center gap-0.5 rounded-sm px-1 py-0 text-left text-sidebar-foreground/90 hover:bg-sidebar-accent/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-sidebar-ring"
          >
            <span
              className="flex h-5 w-5 flex-none items-center justify-center text-muted-foreground"
              aria-hidden
            >
              {openTabsSectionCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </span>
            <span className="text-[11px] font-bold uppercase tracking-widest text-sidebar-foreground/85">
              Open Tabs
            </span>
            <span className="ml-0.5 text-[10px] font-medium tabular-nums text-muted-foreground">
              ({openTabsTotalCount})
            </span>
          </button>
        </div>
      </div>

      {onToggleWorkspace && (
        <WorkspaceRowContextMenuPortal
          menu={wsRowMenu}
          menuRef={wsRowMenuRef}
          onClose={closeWsRowMenu}
          onToggleWorkspace={onToggleWorkspace}
          onHideOtherWorkspaces={onHideOtherWorkspaces}
          activeWorkspaceIds={activeWorkspaceIds}
          workspaceHiddenFromScope={
            wsRowMenu != null && !activeWorkspaceIds.includes(wsRowMenu.wsId)
          }
        />
      )}
    </>
  )
})
